import { useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';
import {
  ActivityLabelOptionsDocument,
  ActivityLabelOptionsQuery,
  TimesheetLabelValueSearchInput,
  useActivityLabelOptionsLazyQuery,
  WbsLabelValueSearchResult,
  ActivityLabelOptionsQueryResult
} from 'graphql/types';
import { FacetOption } from 'modules/notes/common';
import { SearchHandlerParams } from '../../LabelFacetDetails';

const ACTIVITY_PAGE_COUNT = 50;

export interface ActivityLabelSearchParams {
  query?: string;
}

export interface UseActivityLabelSearchResultProps {
  options: WbsLabelValueSearchResult[];
  loading: boolean;
  searchHandler: (params: SearchHandlerParams) => void;
  hasMore: boolean;
}

const mapActivityOptions = (
  data: ActivityLabelOptionsQuery | undefined
): WbsLabelValueSearchResult[] =>
  (data?.timesheetLabelValueSearch?.activities?.items || []).map(
    ({ id, name }) => ({
      id,
      displayText: name!
    })
  );

export const useActivityLabelFacetSearch = (): UseActivityLabelSearchResultProps => {
  const [getActivities, { data, loading }] = useActivityLabelOptionsLazyQuery();

  const searchHandler = useCallback(
    ({ query }: SearchHandlerParams) => {
      const variables = {
        date: DateTime.local().toISODate(),
        input: {
          count: ACTIVITY_PAGE_COUNT,
          query: query
        }
      };

      getActivities({ variables });
    },
    [getActivities]
  );

  return useMemo(
    () => ({
      searchHandler,
      options: mapActivityOptions(data),
      loading,
      hasMore: Boolean(data?.timesheetLabelValueSearch?.activities?.cursor)
    }),
    [data, loading, searchHandler]
  );
};

export const searchActivities = ({
  client: apolloActivity
}: {
  client: any;
}) => async (
  input: TimesheetLabelValueSearchInput
): Promise<WbsLabelValueSearchResult[]> => {
  const { data }: ActivityLabelOptionsQueryResult = await apolloActivity.query({
    query: ActivityLabelOptionsDocument,
    variables: {
      date: DateTime.local().toISODate(),
      input: input
    }
  });

  return mapActivityOptions(data);
};

const transformActivity = (
  data: ActivityLabelOptionsQuery | undefined
): FacetOption[] =>
  (data?.timesheetLabelValueSearch?.activities?.items || []).map(
    (activity) => ({
      ...activity,
      label: activity.name!,
      key: activity.name!,
      id: activity.id || undefined
    })
  );

export const searchActivitiesByTerm = ({
  client: apolloActivity
}: {
  client: any;
}) => async (searchTerm: string): Promise<FacetOption[]> => {
  const { data }: ActivityLabelOptionsQueryResult = await apolloActivity.query({
    query: ActivityLabelOptionsDocument,
    variables: {
      date: DateTime.local().toISODate(),
      input: { count: 10, query: searchTerm }
    }
  });

  return transformActivity(data);
};

export default useActivityLabelFacetSearch;
